import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
  Box,
  Card,
  CardActions,
  CardContent,
  CardHeader,
  Divider,
  IconButton,
  Stack,
  Tooltip,
  Typography,
  Button,
  CircularProgress,
} from '@mui/material';
import { brandColors } from 'types/colors';
import ArrowOutwardIcon from '@mui/icons-material/ArrowOutward';
import Chart from 'react-apexcharts';
import { useDispatch, useSelector } from 'react-redux';
import { fetchImpactData } from 'stores/sensor/homePageSlice';
import ApartmentOutlinedIcon from '@mui/icons-material/ApartmentOutlined';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import KeyboardArrowLeft from '@mui/icons-material/KeyboardArrowLeft';
import KeyboardArrowRight from '@mui/icons-material/KeyboardArrowRight';
import GenericModal from 'components/GenericModal';
import SavingsDetails from './modals/SavingsDetails';
import { calculateOnWindowHoursForRange } from 'helpers/helpers';
// Import the confetti library
import confetti from 'canvas-confetti';

const actionButtonSx = {
  textTransform: 'none',
  fontWeight: 600,
  px: 2.5,
  py: 1,
  border: `1px solid ${brandColors.BLUE}`,
  color: brandColors.BLUE,
  backgroundColor: '#fff',
  boxShadow: '0 8px 18px rgba(20, 60, 160, 0.25)',
  '&:hover': {
    color: brandColors.BLUE_DARK,
    backgroundColor: brandColors.BLUE_DARK,
    boxShadow: '0 10px 22px rgba(20, 60, 160, 0.35)',
  },
};

const formatMetricValue = value => {
  if (!Number.isFinite(value)) {
    return '—';
  }
  if (Number.isInteger(value)) {
    return String(value);
  }
  return value
    .toFixed(2)
    .replace(/\.00$/, '')
    .replace(/(\.\d)0$/, '$1');
};

const formatOneDecimalTrim = value => {
  if (!Number.isFinite(value)) return '—';
  const rounded = Math.round(value * 10) / 10;
  return rounded.toFixed(1).replace(/\.0$/, '');
};

const reductionOptions = {
  chart: { type: 'donut' },
  labels: ['Energy Saved', 'Energy Used'],
  colors: [brandColors.GREEN, brandColors.TURQUOISE_LIGHT],
  legend: { show: false },
  dataLabels: { enabled: false },
  stroke: { width: 0 },
  plotOptions: {
    pie: {
      donut: {
        size: '82%',
      },
    },
  },
  tooltip: {
    fixed: {
      enabled: true,
      position: 'topCenter',
      offsetY: 0,
      offsetX: 0,
    },
  },
};

const parseScheduleObject = value => {
  if (value && typeof value === 'object' && !Array.isArray(value)) return value;
  if (typeof value === 'string') {
    const trimmed = value.trim();
    if (!trimmed) return {};
    try {
      const parsed = JSON.parse(trimmed);
      if (parsed && typeof parsed === 'object' && !Array.isArray(parsed)) return parsed;
    } catch (e) {
      return {};
    }
  }
  return {};
};

const ImpactCard = () => {
  const dispatch = useDispatch();
  const rangeObj = useSelector(state => state.homePage.homePageDatesObj);
  const filterState = useSelector(state => state.persistence?.selectedFilterObject);
  const buildingsDetails = useSelector(state => state.sensors?.buildings_details);
  const userBuildings = useSelector(state => state.users?.buildings);
  const filteredDevices = useSelector(state => state.sensors?.filteredDevices);
  const building_id = filterState?.building;
  const floor_number = filterState?.floor;
  const hvacEquipment = filterState?.hvacEquipment;
  const userTimeZone = Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC';
  const impactData = useSelector(state => state.homePage.impactData);
  const impactLoading = useSelector(state => state.homePage.impactLoading);
  const [savedImpactData, setSavedImpactData] = useState(null);
  const [modalState, setModalState] = useState({ open: false, config: null });
  const [energyReductionView, setEnergyReductionView] = useState('summary');
  const totalKwhWaste = Number(impactData?.total_kwh_5min ?? 0);
  const totalKwhWastePotential = impactData?.total_kwh_waste_potential;
  const energySaved = totalKwhWastePotential - totalKwhWaste;
  const co2Avoided = Math.round(0.459 * energySaved * 10) / 10;
  const costSaved = Math.round(0.56 * energySaved);
  const energySavedPercent =
    totalKwhWastePotential > 0 ? Math.max(0, Math.min(100, (energySaved / totalKwhWastePotential) * 100)) : null;
  const energyUsedPercent =
    totalKwhWastePotential > 0 ? Math.max(0, Math.min(100, (totalKwhWaste / totalKwhWastePotential) * 100)) : 0;
  const selectedBuildingSchedule = useMemo(() => {
    const sourceLists = [buildingsDetails, userBuildings].filter(Array.isArray);
    for (const list of sourceLists) {
      const found = list.find(b => String(b?.id ?? b?.building_id ?? b?.value ?? '') === String(building_id));
      if (found) return found;
    }
    return null;
  }, [buildingsDetails, userBuildings, building_id]);
  const selectedBuildingCavStart = useMemo(
    () => parseScheduleObject(selectedBuildingSchedule?.cav_start_time),
    [selectedBuildingSchedule],
  );
  const selectedBuildingCavEnd = useMemo(
    () => parseScheduleObject(selectedBuildingSchedule?.cav_end_time),
    [selectedBuildingSchedule],
  );
  const totalOnHours = calculateOnWindowHoursForRange(selectedBuildingCavStart, selectedBuildingCavEnd, rangeObj);
  const selectedControllersCount = useMemo(() => {
    if (!Array.isArray(filteredDevices)) return 0;
    return filteredDevices.filter(device =>
      String(device?.device_type || '')
        .toLowerCase()
        .includes('controller'),
    ).length;
  }, [filteredDevices]);
  const avgOnRecordsCount = Number(
    impactData?.avg_number_of_on_records ?? (impactData?.number_of_on_records ?? 0) / Math.max(1, selectedControllersCount) ?? 0,
  );
  const avgOnHoursNumber = Number.isFinite(avgOnRecordsCount) ? (avgOnRecordsCount * 5) / 60 : 0;
  const safeOnHours = Number.isFinite(avgOnHoursNumber) && avgOnHoursNumber > 0 ? avgOnHoursNumber : 0;
  const boundedOnHours = Math.min(safeOnHours, Math.max(0, totalOnHours));
  const onHoursPercent = totalOnHours > 0 ? (boundedOnHours / totalOnHours) * 100 : null;

  const moneyValueRef = useRef(null);
  const confettiTriggeredRef = useRef(false);
  // const [data, setData] = useState(null);

  // Trigger confetti when data loads successfully
  useEffect(() => {
    if (impactLoading) {
      confettiTriggeredRef.current = false;
      return;
    }
    if (!impactLoading && impactData && energySaved > 0 && !confettiTriggeredRef.current) {
      confettiTriggeredRef.current = true;
      requestAnimationFrame(() => {
        const rect = moneyValueRef.current?.getBoundingClientRect();
        const origin = rect
          ? {
              x: (rect.left + rect.width / 2) / window.innerWidth,
              y: (rect.top + rect.height / 2) / window.innerHeight,
            }
          : { y: 0.6 };

        confetti({
          particleCount: 35,
          spread: 45,
          startVelocity: 18,
          ticks: 200,
          scalar: 0.6,
          gravity: 0.45,
          decay: 0.92,
          origin,
          // colors: [brandColors.GREEN, brandColors.TURQUOISE, '#ffffff'],
          colors: [brandColors.GREEN],
        });
      });
    }
  }, [impactLoading, impactData, energySaved]);

  const impactMetrics = [
    {
      icon: require('assets/images/icons/flash.png'),
      title: 'Energy Saved',
      unit: 'kWh',
      value: energySaved,
    },
    {
      icon: require('assets/images/icons/co2-cloud.png'),
      title: 'tCO₂e Avoided',
      unit: 'KG',
      value: co2Avoided,
    },
    {
      icon: require('assets/images/icons/money.png'),
      title: 'Cost Saved',
      unit: '₪',
      value: costSaved,
    },
  ];

  useEffect(() => {
    if (!building_id || building_id === 'undefined' || building_id === 'all') {
      return;
    }
    if (!rangeObj) {
      return;
    }

    dispatch(fetchImpactData({ building_id, floor_number, hvacEquipment, timeRange: rangeObj, userTimeZone }))
      .unwrap()
      .then(data => {
        // setData(data);
      })
      .catch(error => {
        console.error('Impact data fetch failed:', error);
      });
  }, [dispatch, building_id, floor_number, hvacEquipment, rangeObj, userTimeZone]);

  useEffect(() => {
    if (!impactLoading && impactData) {
      setSavedImpactData(impactData);
    }
  }, [impactLoading, impactData]);

  const safeEnergySavedPercent = Number.isFinite(energySavedPercent) ? energySavedPercent : 0;
  const reductionSeries = [safeEnergySavedPercent, Math.max(0, 100 - safeEnergySavedPercent)];
  const handleOpenModal = key => {
    setModalState({ open: true, config: modalConfigs[key] || null });
  };
  const handleCloseModal = () => {
    setModalState(s => ({ ...s, open: false }));
  };
  const modalConfigs = {
    savingsDetails: {
      title: (
        <Stack direction="row" spacing={1} alignItems="center">
          <span>Savings Details</span>
          <Tooltip
            title="All data in this table is calculated only from the building configured CAV hours."
            arrow
            PopperProps={{
              sx: { zIndex: 50001 },
            }}>
            <IconButton size="small" aria-label="Savings details calculation info" sx={{ p: 0.25, color: brandColors.GREY }}>
              <InfoOutlinedIcon sx={{ fontSize: 18 }} />
            </IconButton>
          </Tooltip>
        </Stack>
      ),
      Component: SavingsDetails,
      componentProps: {
        buildingId: building_id,
        floorNumber: floor_number,
        timeRange: rangeObj,
        totalCavHours: Number(savedImpactData?.total_cav_hours ?? 0),
        cavActiveHours: {
          cav_start_time: selectedBuildingCavStart,
          cav_end_time: selectedBuildingCavEnd,
        },
        onClose: handleCloseModal,
      },
      paperSx: { width: 'min(1628px, 95vw)', maxHeight: '80vh' },
    },
  };
  const donutOptions = {
    ...reductionOptions,
    tooltip: {
      enabled: true,
      fixed: {
        enabled: true,
        position: 'topCenter',
        offsetY: 0,
        offsetX: 0,
      },
      custom: function ({ seriesIndex }) {
        const actual = seriesIndex === 0 ? energySaved : totalKwhWaste;
        const value = formatOneDecimalTrim(actual);
        return `<div style="padding:6px 8px;font-weight:600;">${value} kWh</div>`;
      },
    },
  };
  const onHoursDonutOptions = {
    ...reductionOptions,
    labels: ['ON Time', 'Remaining CAV ON Window'],
    colors: [brandColors.GREEN, `${brandColors.GREY}80`],
    tooltip: {
      enabled: true,
      fixed: {
        enabled: true,
        position: 'topCenter',
        offsetY: 0,
        offsetX: 0,
      },
      custom: function ({ seriesIndex }) {
        const actualHours = seriesIndex === 0 ? boundedOnHours : Math.max(0, totalOnHours - boundedOnHours);
        const value = formatOneDecimalTrim(actualHours);
        return `<div style="padding:6px 8px;font-weight:600;">${value} hrs</div>`;
      },
    },
  };
  const onHoursSeries = totalOnHours > 0 ? [boundedOnHours, Math.max(0, totalOnHours - boundedOnHours)] : [0, 1];
  const isSummaryView = energyReductionView === 'summary';

  if (!building_id || building_id === 'undefined' || building_id === 'all') {
    return (
      <Card
        sx={{
          width: '100%',
          height: '100%',
          minHeight: 0,
          borderRadius: 3,
          overflow: 'hidden',
          border: `2px solid ${brandColors.GREY_LIGHT}`,
          display: 'flex',
          flexDirection: 'column',
        }}>
        <CardHeader
          title="Energy Optimization Impact"
          subheader="Savings are computed from VEM using controller telemetry and CAV baseline modeling."
          sx={{
            background: brandColors.BLUE,
            color: 'white',
            // minHeight: 'fit-content',
            '& .MuiCardHeader-title': { fontWeight: 600, color: 'rgba(255, 255, 255, 0.85)' },
            '& .MuiCardHeader-subheader': { color: 'rgba(255, 255, 255, 0.85)', fontSize: '1rem' },
            // py: 1.5,
          }}
        />
        <CardContent
          sx={{
            flex: 1,
            minHeight: 0,
            py: 4,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            overflowY: 'auto',
            overflowX: 'hidden',
          }}>
          <Stack spacing={2} alignItems="center" sx={{ textAlign: 'center' }}>
            <Box
              sx={{
                height: 64,
                width: 64,
                borderRadius: '50%',
                backgroundColor: brandColors.GREY_LIGHT,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}>
              <ApartmentOutlinedIcon sx={{ fontSize: 34, color: brandColors.BLUE }} />
            </Box>
            <Typography variant="subtitle1" sx={{ fontWeight: 600, color: '#282828' }}>
              Select a building to view impact data
            </Typography>
            <Stack direction="row" spacing={1} alignItems="center" sx={{ color: brandColors.GREY }}>
              <InfoOutlinedIcon sx={{ fontSize: 18 }} />
              <Typography variant="body2" sx={{ color: brandColors.GREY }}>
                Use the filters above to choose a building and time range.
              </Typography>
            </Stack>
          </Stack>
        </CardContent>
      </Card>
    );
  }

  return (
    <>
      <GenericModal open={modalState.open} onClose={handleCloseModal} {...(modalState.config || {})} />
      <Card
        sx={{
          width: '100%',
          height: '100%',
          minHeight: 0,
          borderRadius: 3,
          overflow: 'hidden',
          border: `2px solid ${brandColors.GREY_LIGHT}`,
          display: 'flex',
          flexDirection: 'column',
        }}>
        <CardHeader
          title="Energy Optimization Impact"
          subheader="Savings are computed from VEM using controller telemetry and CAV baseline modeling."
          sx={{
            background: brandColors.BLUE,
            color: 'white',
            // minHeight: 'fit-content',
            '& .MuiCardHeader-title': { fontWeight: 600, color: '#fff' },
            '& .MuiCardHeader-subheader': { color: '#fff', fontSize: '1rem', fontWeight: 400 },
            py: 1,
          }}
          
        />
        <CardContent
          sx={{
            px: 0,
            flex: 1,
            minHeight: 0,
            pb: 0,
            display: 'flex',
            flexDirection: 'column',
            overflowY: 'auto',
            overflowX: 'hidden',
          }}>
          <Stack spacing={0} sx={{ flex: 1, justifyContent: 'center' }}>
            <Typography variant="subtitle1" sx={{ fontWeight: 600, color: '#282828', pl: 1.5 }}>
              Verified savings DCV vs CAV baseline — for selected time range
            </Typography>
            <Box
              sx={{
                display: 'grid',
                gridTemplateColumns: { xs: 'repeat(3, minmax(0, 1fr))', md: 'repeat(4, 1fr)' },
                gap: 0,
                alignItems: { lg: 'center' },
              }}>
              {impactMetrics.map((metric, index) => (
                <Box
                  key={metric.title}
                  id={metric.title}
                  sx={{
                    borderRight: {
                      xs: index < impactMetrics.length - 1 ? `1px solid ${brandColors.GREY}40` : 'none',
                      md: `2px solid ${brandColors.GREY}40`,
                    },
                    borderBottom: {
                      xs: 'none',
                      md: 'none',
                    },
                    pr: { xs: 0, md: 2 },
                    py: { xs: 1.25, md: 0 },
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                  }}>
                  <Stack
                    spacing={{ xs: 0.5, lg: 0.35 }}
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      width: '100%',
                    }}>
                    <Box
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                      }}>
                      <Box
                        sx={{
                          height: 35,
                          width: 35,
                          backgroundColor: brandColors.GREEN,
                          maskImage: `url(${metric.icon})`,
                          maskSize: 'contain',
                          maskRepeat: 'no-repeat',
                          maskPosition: 'center',
                          WebkitMaskImage: `url(${metric.icon})`,
                          WebkitMaskSize: 'contain',
                          WebkitMaskRepeat: 'no-repeat',
                          WebkitMaskPosition: 'center',
                        }}
                      />
                    </Box>
                    <Typography
                      variant="body1"
                      sx={{
                        color: '#282828',
                        fontWeight: 400,
                        fontSize: { xs: '1rem', sm: '1rem', md: '1.25rem' },
                        lineHeight: 1.15,
                        textAlign: 'center',
                        minHeight: { xs: '3.8rem', lg: '2.1rem' },
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                      }}>
                      {metric.title}
                    </Typography>
                    <Stack
                      direction="row"
                      spacing={0.75}
                      alignItems="center"
                      justifyContent="center"
                      sx={{ minHeight: { xs: '2.2rem', lg: '2.1rem' } }}>
                      {metric.unit === '₪' && (
                        <Typography
                          sx={{
                            fontWeight: 400,
                            color: brandColors.GREEN,
                            fontSize: { xs: '1.2rem', sm: '1.45rem', md: '2rem' },
                            lineHeight: 1,
                          }}>
                          {metric.unit}
                        </Typography>
                      )}
                      <Typography
                        variant="h2"
                        ref={metric.unit === '₪' ? moneyValueRef : null}
                        sx={{
                          color: brandColors.GREEN,
                          fontWeight: 500,
                          textShadow: '0px 0px 5px #0000002e',
                          position: 'relative',
                          fontSize: { xs: '1.3rem', sm: '2rem', md: undefined },
                          lineHeight: 1,
                          display: 'flex',
                          alignItems: 'center',
                        }}>
                        {impactLoading ? (
                          <CircularProgress size={24} thickness={5} sx={{ color: brandColors.GREEN }} />
                        ) : (
                          formatMetricValue(metric.value)
                        )}
                      </Typography>
                      {metric.unit !== '₪' && (
                        <Typography
                          sx={{
                            color: brandColors.GREEN,
                            fontWeight: 400,
                            fontSize: { xs: '.9rem', sm: '1.2rem', md: '1.6rem' },
                            textShadow: '0px 0px 5px #0000002e',
                            lineHeight: 1,
                          }}>
                          {metric.unit}
                        </Typography>
                      )}
                      <ArrowOutwardIcon
                        sx={{
                          color: brandColors.GREEN,
                          scale: { xs: '1', md: '1.5' },
                          ml: 0,
                          alignSelf: 'center',
                        }}
                      />
                    </Stack>
                  </Stack>
                </Box>
              ))}
              <Box
                className="energy-reduction"
                sx={{
                  gridColumn: { xs: '1 / -1', md: 'auto' },
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  position: 'relative',
                  pt: { xs: 2, md: 0 },
                }}>
                <Box sx={{ position: 'absolute', top: -8, right: 8, zIndex: 2, display: 'flex' }}>
                  <IconButton
                    size="small"
                    onClick={() => setEnergyReductionView('summary')}
                    disabled={isSummaryView}
                    sx={{ p: 0.1 }}>
                    <KeyboardArrowLeft sx={{ scale: 1.5 }} />
                  </IconButton>
                  <IconButton
                    size="small"
                    onClick={() => setEnergyReductionView('placeholder')}
                    disabled={!isSummaryView}
                    sx={{ p: 0.1 }}>
                    <KeyboardArrowRight sx={{ scale: 1.5 }} />
                  </IconButton>
                </Box>
                <Box sx={{ width: '100%', maxWidth: 340, overflow: 'hidden', mx: 'auto' }}>
                  <Box
                    sx={{
                      display: 'flex',
                      width: '200%',
                      transform: isSummaryView ? 'translateX(0%)' : 'translateX(-50%)',
                      transition: 'transform 0.35s ease',
                    }}>
                    <Box sx={{ width: '50%', flexShrink: 0 }}>
                      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} alignItems="center" justifyContent="center">
                        <Box
                          sx={{
                            position: 'relative',
                            width: 160,
                            height: 160,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            mx: 'auto',
                          }}>
                          <Chart type="donut" options={donutOptions} series={reductionSeries} width={160} height={160} />
                          <Box
                            sx={{
                              position: 'absolute',
                              inset: 0,
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              pointerEvents: 'none',
                            }}>
                            <Stack spacing={0.5} alignItems="center">
                              <Typography
                                variant="subtitle1"
                                sx={{
                                  fontWeight: 600,
                                  color: '#000',
                                  lineHeight: 1,
                                  fontSize: '2rem',
                                }}>
                                {energySavedPercent === null ? '—' : `${formatOneDecimalTrim(energySavedPercent)}%`}
                              </Typography>
                              <Typography variant="subtitle1" sx={{ fontWeight: 400, color: '#000', fontSize: '.75rem' }}>
                                Energy Reduction
                              </Typography>
                            </Stack>
                          </Box>
                        </Box>
                        <Stack
                          spacing={0.5}
                          alignItems={{
                            xs: 'center',
                            sm: 'flex-start',
                            marginLeft: '0 !important',
                            width: '9rem',
                          }}>
                          <Typography
                            variant="subtitle2"
                            sx={{
                              color: brandColors.GREY_DARK,
                              fontWeight: 600,
                              fontSize: '.8em',
                            }}>
                            Saved / Baseline
                          </Typography>
                          <Typography variant="h5" sx={{ color: '#111', fontWeight: 700, lineHeight: 1.1 }}>
                            {formatMetricValue(energySaved)}
                            <Typography component="span" sx={{ fontSize: '0.8rem', fontWeight: 300, color: '#111' }}>
                              kWh
                            </Typography>{' '}
                            / {formatMetricValue(totalKwhWastePotential)}
                            <Typography component="span" sx={{ fontSize: '0.8rem', fontWeight: 300, color: '#111' }}>
                              kWh
                            </Typography>
                          </Typography>
                        </Stack>
                      </Stack>
                    </Box>
                    <Box sx={{ width: '50%', flexShrink: 0 }}>
                      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} alignItems="center" justifyContent="center">
                        <Box
                          sx={{
                            position: 'relative',
                            width: 160,
                            height: 160,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            mx: 'auto',
                          }}>
                          <Chart type="donut" options={onHoursDonutOptions} series={onHoursSeries} width={160} height={160} />
                          <Box
                            sx={{
                              position: 'absolute',
                              inset: 0,
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              pointerEvents: 'none',
                            }}>
                            <Stack spacing={0.5} alignItems="center">
                              <Typography
                                variant="subtitle1"
                                sx={{
                                  fontWeight: 600,
                                  color: '#000',
                                  lineHeight: 1,
                                  fontSize: '2rem',
                                }}>
                                {onHoursPercent === null ? '—' : `${formatOneDecimalTrim(onHoursPercent)}%`}
                              </Typography>
                              <Typography variant="subtitle1" sx={{ fontWeight: 400, color: '#000', fontSize: '.75rem' }}>
                                ON Time Coverage
                              </Typography>
                            </Stack>
                          </Box>
                        </Box>
                        <Stack
                          spacing={0.5}
                          alignItems={{
                            xs: 'center',
                            sm: 'flex-start',
                            marginLeft: '0 !important',
                            width: '9rem',
                          }}>
                          <Typography
                            variant="subtitle2"
                            sx={{
                              color: brandColors.GREY_DARK,
                              fontWeight: 600,
                              fontSize: '.8em',
                            }}>
                            AVG ON Time / Total CAV Time
                          </Typography>
                          <Typography variant="h6" sx={{ color: '#111', fontWeight: 700, lineHeight: 1.1 }}>
                            {formatOneDecimalTrim(boundedOnHours)} hrs / {formatOneDecimalTrim(totalOnHours)} hrs
                          </Typography>
                        </Stack>
                      </Stack>
                    </Box>
                  </Box>
                </Box>
              </Box>
            </Box>
          </Stack>
        </CardContent>
        <Divider sx={{ borderColor: brandColors.GREY, borderWidth: '2px', m: 0 }} />
        <CardActions
          sx={{
            px: 3,
            py: 1,
            display: 'flex',
            flexWrap: 'wrap',
            justifyContent: 'space-between',
            gap: 2,
          }}>
          <Stack spacing={0.75} sx={{ minWidth: 260, maxWidth: 520 }}>
            <Box
              sx={{
                width: '100%',
                height: 12,
                borderRadius: 999,
                backgroundColor: brandColors.GREY_LIGHT,
                overflow: 'hidden',
              }}>
              <Box
                sx={{
                  width: `${energyUsedPercent}%`,
                  height: '100%',
                  background: brandColors.BLUE,
                  transition: 'width 350ms ease',
                }}
              />
            </Box>
            <Typography variant="body2" sx={{ color: '#282828', fontWeight: 400, fontSize: '.95rem' }} className="footer-summary">
              Energy Used:{' '}
              <Box component="span" sx={{ fontWeight: 700 }}>
                {formatMetricValue(totalKwhWaste)}
              </Box>{' '}
              kWh ({energyUsedPercent.toFixed(0)}%)
              {' | '}Baseline:{' '}
              <Box component="span" sx={{ fontWeight: 700 }}>
                {formatMetricValue(totalKwhWastePotential)}
              </Box>{' '}
              kWh
              <Box component="span" sx={{ fontWeight: 700 }}></Box>
            </Typography>
          </Stack>
          <Stack direction="row" spacing={1}>
            <Button disabled={false} variant="inherit" sx={actionButtonSx} onClick={() => handleOpenModal('savingsDetails')}>
              Savings Details
            </Button>
            {/* <Button
              disabled={false}
              variant="outlined"
              sx={{
                textTransform: 'none',
                borderColor: brandColors.BLUE,
                color: brandColors.BLUE,
                '&:hover': { borderColor: brandColors.BLUE },
              }}>
              Export Executive Summary
            </Button> */}
          </Stack>
        </CardActions>
      </Card>
    </>
  );
};

export default ImpactCard;
